import {
  ArrowClockwise,
  DownloadSimple,
  MagnifyingGlass,
  Plus,
  UsersThree,
  WarningCircle,
  XCircle,
} from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { Button } from '@/components/ui-kit/button';
import { TableCell, TableRow } from '@/components/ui-kit/table';

export type ContactsTableStateKind = 'error' | 'empty' | 'noresults';

type ContactsTableStateRowProps = {
  kind: ContactsTableStateKind;
  colSpan: number;
  onRetry?: () => void;
  onAddContact?: () => void;
  onImport?: () => void;
  onClearFilters?: () => void;
};

const STATE_CONTENT: Record<
  ContactsTableStateKind,
  { icon: ReactNode; title: string; body: string }
> = {
  error: {
    icon: <WarningCircle className="size-8 text-error" aria-hidden />,
    title: "Couldn't load contacts",
    body: 'Something went wrong while fetching your contacts. Check your connection and try again.',
  },
  empty: {
    icon: <UsersThree className="size-8 text-neutral-400" aria-hidden />,
    title: 'No contacts yet',
    body: 'Add your first contact manually, or import a CSV to bring in your existing audience.',
  },
  noresults: {
    icon: <MagnifyingGlass className="size-8 text-neutral-400" aria-hidden />,
    title: 'No contacts match',
    body: 'Try a different search term, or clear your filters to see everyone.',
  },
};

export const ContactsTableStateRow = ({
  kind,
  colSpan,
  onRetry,
  onAddContact,
  onImport,
  onClearFilters,
}: ContactsTableStateRowProps) => {
  const content = STATE_CONTENT[kind];

  return (
    <TableRow className="hover:bg-transparent">
      <TableCell colSpan={colSpan} className="px-6 py-14 align-middle">
        <div className="mx-auto flex min-h-[260px] max-w-[360px] flex-col items-center justify-center gap-3 text-center">
          {content.icon}
          <div className="text-lg font-semibold text-neutral-900">{content.title}</div>
          <p className="text-sm text-neutral-600">{content.body}</p>
          <div className="mt-1 flex flex-wrap items-center justify-center gap-2.5">
            {kind === 'error' ? (
              <Button
                type="button"
                className="h-10 gap-2 bg-primary-600 px-4 text-white hover:bg-primary-700"
                onClick={onRetry}
              >
                <ArrowClockwise className="size-4" aria-hidden />
                Retry
              </Button>
            ) : null}
            {kind === 'empty' ? (
              <>
                <Button
                  type="button"
                  className="h-10 gap-2 bg-primary-600 px-4 text-white hover:bg-primary-700"
                  onClick={onAddContact}
                >
                  <Plus className="size-4" aria-hidden />
                  Add contact
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="h-10 gap-2 border-neutral-300 px-4 text-neutral-900 hover:bg-neutral-100"
                  onClick={onImport}
                >
                  <DownloadSimple className="size-4" aria-hidden />
                  Import
                </Button>
              </>
            ) : null}
            {kind === 'noresults' ? (
              <Button
                type="button"
                className="h-10 gap-2 bg-primary-600 px-4 text-white hover:bg-primary-700"
                onClick={onClearFilters}
              >
                <XCircle className="size-4" aria-hidden />
                Clear filters
              </Button>
            ) : null}
          </div>
        </div>
      </TableCell>
    </TableRow>
  );
};
